<template lang="pug">
.zeus-sleeve(@mousedown.stop="" @touchstart.stop="" @click.stop="" ref="root")
  custom-bumper-render-fonts
  //- Add a background image for sizing
  StaticImage(:width="CANVAS_WIDTH" :height="CANVAS_HEIGHT" alt="")
  .zeus-canvas-container
    canvas(ref="work-canvas")
    .print-canvas-container(v-if="generatePrintTemplate")
      canvas(ref="print-canvas")
</template>
<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef, watch, markRaw } from 'vue'
import { Canvas, FabricImage, FabricObject, Rect, Path, util, Group, Point, loadSVGFromURL, Textbox } from 'fabric'
import { useRoute } from 'vue-router'
import { useMQ } from 'sisu/js/mq'
import type { BuyRequest } from '../../../services/types/order'
import { bleedClipPaths } from './kneesleeve-clip'
import CustomBumperRenderFonts from './custom-bumper-render-fonts.vue'
import { canvasToTemplate } from './kneesleeve-util'
import type { CanvasState, ZeusRecipe } from './kneesleeve-types'
import { useProductStore } from '@/js/stores/productStore'
import { useRogue } from '@/js/services/Rogue'

declare module "fabric" {
  // to have the properties recognized on the instance and in the constructor
  interface FabricObject {
    id?: number,
    objType?: 'text' | 'image' | 'backgroundImg',
    dataUrl?: string,
  }
  // to have the properties typed in the exported object
  interface SerializedObjectProps {
    id?: number,
    objType?: 'text' | 'image' | 'backgroundImg',
    dataUrl?: string,
  }
}

FabricObject.customProperties = ['id', 'objType', 'dataUrl']

// We want Fabric to maintain a good z-index for the different layers
const LAYER_ORDER = {
  SLEEVE_COLOR: 0,
}

const CANVAS_WIDTH = 3200
const CANVAS_HEIGHT = 1800

const props = defineProps<{
  buyRequest: BuyRequest,
  canvasState: CanvasState,
}>()

const emit = defineEmits<{ (e: 'loaded'): void, (e: 'update:canvasState', val: CanvasState): void }>()

const rogue = useRogue()
const route = useRoute()
const mq = useMQ()
const productStore = useProductStore()
const updateBuyRequest = inject<(val: BuyRequest) => void>('updateBuyRequest', (_val: BuyRequest) => { })

const rootRef = useTemplateRef<HTMLDivElement>('root')
const workCanvas = useTemplateRef<HTMLCanvasElement>('work-canvas')
const printCanvas = useTemplateRef<HTMLCanvasElement>('print-canvas')

const objCount = ref<number>(0) // assign id's
const generatePrintTemplate = ref<boolean>(false)

// This will control references to the Fabric objects that we need to use
const fabric = reactive({
  canvas      : null as null | Canvas,
  clip        : null as null | Path,
  background  : null as null | FabricImage,
  sleeveColor : null as null | Rect | FabricImage,
})

const products = computed(() => productStore.products)
// This will always tell us the selected sleeve
const simpleId = computed(() => props.buyRequest.children?.[0]?.productId)

async function loadFonts() {
  const ffo = await import(/* webpackChunkName: "fontfaceobserver" */ 'fontfaceobserver')
  const fonts = ['Arvo', 'Arimo', 'Montserrat']
  const promises = []
  fonts.forEach(value => {
    promises.push(new ffo.default(value).load())
  })
}

// Add text to the sleeve. It should be centered and selected
function addText() {
  const text = new Textbox('Text', {
    fontFamily      : 'Arvo',
    fontSize        : 84,
    fontAlias       : 'Medium',
    textAlign       : 'center',
    fill            : 'black',
    centeredScaling : false,
    width           : 800,
    id              : objCount.value,
  })

  text.setControlsVisibility({
    mt : false,
    mb : false,
    bl : false,
    br : false,
    tl : false,
    tr : false,
  })

  text.set({ clipPath: fabric.clip, objType: 'text' })
  fabric.canvas!.viewportCenterObject(text)
  fabric.canvas!.add(text)
  fabric.canvas!.renderAll()

  const updatedState: CanvasState = JSON.parse(JSON.stringify(props.canvasState))
  updatedState.userTexts += 1
  updatedState.totalObjects = fabric.canvas!.getObjects().length - 1
  emit('update:canvasState', updatedState)
  objCount.value += 1

  setLayer(text)
  fabric.canvas!.setActiveObject(text)
}

function setLayer(layer: FabricObject | { objType: 'none', id?: number }) {
  if (layer && Object.keys(layer).length === 0 && Object.getPrototypeOf(layer) === Object.prototype) {
    return
  }
  const al: FabricObject | { objType: 'none', id?: number} = (!layer?.objType ? fabric.canvas!.getActiveObject()! : layer)
  const objects = fabric.canvas!.getObjects() as (FabricObject & { objType: string, id: number})[]
  const position = objects.findIndex(value => al?.id !== undefined && al.id === value.id)
  const updatedState = JSON.parse(JSON.stringify(props.canvasState))
  if (props.canvasState.selectedItem.type === 'text' && (fabric.canvas!.getObjects()[props.canvasState.selectedItem.position] as Textbox)?.text === '') {
    fabric.canvas!.remove(fabric.canvas!.getObjects()[props.canvasState.selectedItem.position])
    updatedState.userTexts -= 1
  }
  if (al?.objType === 'text') {
    updatedState.selectedItem.type = 'text'
    updatedState.selectedItem.text = (al as Textbox).text
    updatedState.selectedItem.font = (al as Textbox).fontFamily
    updatedState.selectedItem.fill = (al as Textbox).fill
    updatedState.selectedItem.size = (al as Textbox).fontSize
    updatedState.selectedItem.img = ''
  } else if (position !== -1 && al?.objType === 'image') {
    updatedState.selectedItem.type = 'img'
    updatedState.selectedItem.img = (al as FabricImage).getSrc()
  } else {
    updatedState.selectedItem.type = 'none'
  }
  updatedState.selectedItem.position = position
  updatedState.totalObjects = objects.length - 1
  // calculate if the object is first or last in the object stack
  // ignore the group, backgroundImg, and background color (objects [0] -> [2])
  updatedState.selectedItem.first = updatedState.selectedItem.position === 1
  updatedState.selectedItem.last = updatedState.selectedItem.position === updatedState.totalObjects
  emit('update:canvasState', updatedState)
}

// Add an image, centered on the sleeve
async function addImage(url: string) {
  const img = await FabricImage.fromURL(url)

  img.set({ id: objCount.value, dataUrl: img.toDataURL({}), objType: 'image', clipPath: fabric.clip })

  fabric.canvas!.add(img)
  fabric.canvas!.renderAll()
  const ol = fabric.canvas!.getObjects().length
  if (ol > 1) {
    setLayer(img)
  }

  fabric.canvas!.viewportCenterObject(img)
  const updatedState = JSON.parse(JSON.stringify(props.canvasState))
  updatedState.userImages += 1
  updatedState.totalObjects = fabric.canvas!.getObjects().length - 1
  emit('update:canvasState', updatedState)
  objCount.value += 1

  fabric.canvas!.setActiveObject(img)
}

async function setTextureBackground(texture: string) {
  const url = `/svc_ssr/cloudinary_proxy/zeus%2F__assets%2Fkneesleeves%2F${texture}.png?version=v1681216391`
  const img = await FabricImage.fromURL(url)
  img.set({ 
    clipPath   : fabric.clip, 
    selectable : false, 
    objType    : 'backgroundImg',
    left       : CANVAS_WIDTH / 2,
    top        : CANVAS_HEIGHT / 2,
  })
  fabric.canvas!.remove(fabric.canvas!.getObjects()[LAYER_ORDER.SLEEVE_COLOR])
  fabric.canvas!.insertAt(LAYER_ORDER.SLEEVE_COLOR, img)
}

function setBackgroundColor(fill: string) {
  if (fabric.canvas!.getObjects()[LAYER_ORDER.SLEEVE_COLOR]?.objType === 'backgroundImg') {
    const sleeveColor = markRaw(new Rect({
      fill,
      width       : CANVAS_WIDTH,
      height      : CANVAS_HEIGHT,
      selectable  : false,
      hoverCursor : 'default',
      left        : CANVAS_WIDTH / 2,
      top         : CANVAS_HEIGHT / 2,
    }))
    sleeveColor.clipPath = fabric.clip as Path
    fabric.sleeveColor = sleeveColor
    fabric.canvas!.remove(fabric.canvas!.getObjects()[LAYER_ORDER.SLEEVE_COLOR])
    fabric.canvas!.insertAt(LAYER_ORDER.SLEEVE_COLOR, sleeveColor)
  } else {
    fabric.sleeveColor!.fill = fill
    fabric.canvas!.renderAll()
  }
}

async function changeSleeveSize() {
  // Get the selected size from the current buy request product
  const size = products.value[simpleId.value!].attributes.generic_size as string

  // The URL for the image in cloudinary is based on the sleeve size
  // https://assets.roguefitness.com/image/upload/v1681216392/zeus/__assets/kneesleeves/${size}/
  const url = `/svc_ssr/cloudinary_proxy/zeus%2F__assets%2Fkneesleeves_v2%2F${size.toLowerCase()}.png`

  // Remove the existing background from the canvas
  fabric.background?.dispose()

  // Set the clip path
  const clipPath = bleedClipPaths[size.toLowerCase()]
  if (clipPath) {
    const clip = markRaw(new Path(clipPath, { absolutePositioned: true }))
    // Set the clip on both the sleeve color and the content group
    fabric.canvas!.getObjects().forEach(value => {
      value.clipPath?.dispose()
      value.clipPath = clip
    })
    fabric.sleeveColor!.clipPath = clip
    fabric.clip = clip
  }

  // Create the new background fabric Image object (will load from net if needed)
  const bg = markRaw(await FabricImage.fromURL(url))
  // Backgrounds are not selectable
  bg.selectable = false
  
  bg.set({
    left : CANVAS_WIDTH / 2,
    top  : CANVAS_HEIGHT / 2,
  })

  // Save in a variable so we can dispose of it later if it changes
  fabric.background = bg
  fabric.canvas!.overlayImage = bg
  fabric.canvas!.renderAll()
}

function resize() {
  // Resize the canvas to match the parent element
  /**
   * We can pick our default canvas size at 1600x900 (16:9 ratio) to match the PDP gallery view.
   * The "zoom level" is then ratio between the current image size and the default.
   *
   * Setting zoom once means that every generated canvas will be the same regardless of screen size of the customer.
   * As far as FabricJS is concerned, the position of layer is ALWAYS based on 1600x900 canvas size.
   *
   * Since our background images are 1600x900 exactly, that means they are always sized correctly.
   *
   * Then we just zoom in and out of the canvas as needed to match the actual screen size.
   */
  fabric.canvas!.setDimensions({ width: rootRef.value!.clientWidth, height: rootRef.value!.clientHeight })

  fabric.canvas!.setZoom(rootRef.value!.clientWidth / CANVAS_WIDTH)
}

function updateTextColor(color: string) {
  const textObject = fabric.canvas!.getObjects()[props.canvasState.selectedItem.position]
  textObject.set('fill', color)
  fabric.canvas!.renderAll()
  setLayer(textObject)
}

function updateTextSize(value: 'plus' | 'minus') {
  const textObject = fabric.canvas!.getObjects()[props.canvasState.selectedItem.position]
  const adder = value === 'plus' ? 16 : -16
  if (adder + props.canvasState.selectedItem.size > 36 && adder + props.canvasState.selectedItem.size < 200) {
    textObject.set('fontSize', props.canvasState.selectedItem.size + adder)
  }
  fabric.canvas!.renderAll()
  setLayer(textObject)
}

function updateTextFont(font: string) {
  const textObject = fabric.canvas!.getObjects()[props.canvasState.selectedItem.position]
  textObject.set('fontFamily', font)
  fabric.canvas!.renderAll()
  setLayer(textObject)
}

// https://github.com/fabricjs/fabric.js/issues/8299
// fabric 6 doesn't currently have docs, bringObjectForward and sendObjectBackwards don't have documentation
function moveForward() {
  const fObj = fabric.canvas!.getObjects()[props.canvasState.selectedItem.position]
  fabric.canvas!.bringObjectForward(fObj)
  setLayer(fObj)
}

function moveBackwards() {
  const fObj = fabric.canvas!.getObjects()[props.canvasState.selectedItem.position]
  fabric.canvas!.sendObjectBackwards(fObj)
  setLayer(fObj)
}

function remove() {
  fabric.canvas!.remove(fabric.canvas!.getObjects()[props.canvasState.selectedItem.position])

  const updatedState = JSON.parse(JSON.stringify(props.canvasState))
  if (props.canvasState.selectedItem.type === 'text') {
    updatedState.userTexts -= 1
  } else {
    updatedState.userImages -= 1
  }
  emit('update:canvasState', updatedState)

  fabric.canvas!.renderAll()
  setLayer({ objType: 'none' })
}

async function generateFabricObject() {
  fabric.canvas!.discardActiveObject()
  generatePrintTemplate.value = true
  const size = products.value[simpleId.value!].attributes.generic_size as string
  const json = fabric.canvas!.toObject(['rogueImage', 'selectable', 'id', 'fontFamily', 'url', 'clipPath', 'objType'])
  json.size = size
  const zoom = fabric.canvas!.getZoom()
  const clip = new Path(bleedClipPaths[size.toLowerCase()], { absolutePositioned: true })
  const thumbUrl = fabric.canvas!.toDataURL({
    multiplier : 1,
    width      : zoom * CANVAS_WIDTH / 2,
    height     : zoom * CANVAS_HEIGHT,
    left       : zoom * CANVAS_WIDTH / 4,
  })
  // temporarily hide the sleeve so it doesn't show up in the print file
  fabric.canvas!.overlayImage!.opacity = 0

  fabric.canvas!.getObjects().forEach(value => {
    value.clipPath = clip
  })

  const printUrl = fabric.canvas!.toDataURL({
    multiplier : 2.5,
    width      : zoom * CANVAS_WIDTH / 2,
    height     : zoom * CANVAS_HEIGHT,
    left       : zoom * CANVAS_WIDTH / 4,
  })

  const print = await canvasToTemplate(printUrl, size.toLowerCase(), printCanvas.value!)
  fabric.canvas!.overlayImage!.opacity = 1
  generatePrintTemplate.value = false

  // Reset clipPath and sleeve values since we manipulated them to generate the print file
  await changeSleeveSize()

  return {
    json,
    thumbUrl,
    print,
  }
}

async function addRogueImage(svgObject: { scale: number, type: string, fill: string, url: string }) {
  if (props.canvasState.rImagePosition > 0) {
    fabric.canvas!.remove(fabric.canvas!.getObjects()[props.canvasState.rImagePosition])
  }

  const output = await loadSVGFromURL(`svc_spa/assets/custom/kneesleeves/${svgObject.url}`)
  const image: Group = util.groupSVGElements(output.objects.filter(obj => obj !== null)) as Group
  if (svgObject.type !== 'zeus' && svgObject.type !== 'international') {
    image.getObjects().forEach(path => {
      path.fill = svgObject.fill
    })
  }

  image.set({ id: objCount.value, selectable: false, hoverCursor: 'default', clipPath: fabric.clip })
  image.scaleToHeight(CANVAS_HEIGHT * svgObject.scale)
  // zeus goes in the middle of the sleeve, all others go on the lower half of the sleeve
  const y = svgObject.type === 'zeus' ? CANVAS_HEIGHT / 2 : CANVAS_HEIGHT - (CANVAS_HEIGHT * 0.30)
  const point = new Point(CANVAS_WIDTH / 2, y)
  image.setPositionByOrigin(point, 'center', 'center')

  fabric.canvas!.add(image)
  fabric.canvas!.renderAll()

  const updatedState = JSON.parse(JSON.stringify(props.canvasState))
  updatedState.rImagePosition = fabric.canvas!.getObjects().length - 1
  emit('update:canvasState', updatedState)
  objCount.value += 1
}

watch(simpleId, () => {
  // If the product changes, we need to update the background image
  changeSleeveSize()
})

onMounted(async () => {
  if (!workCanvas.value) return
  
  const canvas = markRaw(new Canvas(workCanvas.value!, {
    controlsAboveOverlay : false,
    selection            : mq.value !== 'mobile',
  }))

  canvas.on('mouse:up', (e) => {
    setLayer(e.target!)
  })

  document.onkeydown = (e) => {
    if (e.key === 'Backspace') {
      if (props.canvasState.selectedItem.type !== 'none' && props.canvasState.selectedItem.type !== 'text') {
        remove()
      }
    }
  }

  const fromRecipe = route.query?.rid
  // Load font face observer
  await loadFonts()
  let sleeveColor: Rect | FabricImage
  if (fromRecipe) {
    const resp = await rogue.fetch<ZeusRecipe>(`/svc_zeus/recipes/${route.query.rid}`, 'GET')
    const fabricConfig = resp.fabricConfig

    const renders = resp.renders

    // change image src from blobs to cloudinary host

    const userImages = renders.filter(value => value.key.indexOf('user-image') !== -1)

    const sources = userImages.map((__, index) => `/svc_ssr/cloudinary_proxy/zeus%2F${route.query?.rid}%2Fuser-image-${index}.png?version=v1681411074`)

    fabricConfig.objects.filter((fObj: any) => fObj.objType === 'image').forEach((value: any, index: number) => {
      value.src = sources[index]
    })

    await canvas.loadFromJSON(fabricConfig)
    sleeveColor = canvas.getObjects()[LAYER_ORDER.SLEEVE_COLOR] as Rect | FabricImage

    const recipeCanvasState = fabricConfig.canvasState

    const updatedState = JSON.parse(JSON.stringify(props.canvasState))
    updatedState.userImages = recipeCanvasState.userImages
    updatedState.userTexts = recipeCanvasState.userTexts
    updatedState.totalObjects = recipeCanvasState.totalObjects
    updatedState.selectedItem = recipeCanvasState.selectedItem
    updatedState.rImagePosition = recipeCanvasState.rImagePosition
    if (recipeCanvasState.rImagePosition > 0) {
      updatedState.tab = 'Add Rogue Design'
    }
    updatedState.selectedColor = recipeCanvasState.selectedColor
    emit('update:canvasState', updatedState)
    await nextTick()

    if (props.buyRequest.children?.[0]) {
      const updatedBuyRequest: BuyRequest = JSON.parse(JSON.stringify(props.buyRequest))
      updatedBuyRequest.children![0].productId = resp.productId
      updateBuyRequest(updatedBuyRequest)
    }
    const size = fabricConfig.size
    const clip = markRaw(new Path(bleedClipPaths[size.toLowerCase()], { absolutePositioned: true }))
    fabric.clip = clip
  } else {
    // create a group for the contents. It will go above the background.
    sleeveColor = markRaw(new Rect({
      fill        : 'white',
      width       : CANVAS_WIDTH,
      height      : CANVAS_HEIGHT,
      selectable  : false,
      hoverCursor : 'default',
      left        : CANVAS_WIDTH / 2,
      top         : CANVAS_HEIGHT / 2,
    }))
    // Insert the als for the sleeve color and content groups.
    canvas.insertAt(LAYER_ORDER.SLEEVE_COLOR, sleeveColor)
  }

  // Save in the `contents` variable for future reference.
  fabric.canvas = canvas
  fabric.sleeveColor = sleeveColor

  if (!fromRecipe) {
    await changeSleeveSize()
  }

  if (props.canvasState.selectedColor === 'tigerstripewoodlandcamo') {
    await setTextureBackground('woodland')
  }

  if (props.canvasState.selectedColor === 'tekgraycamo') {
    await setTextureBackground('tekgray')
  }

  // When the window resizes, update the canvas size
  window.addEventListener('resize', resize)
  resize()

  objCount.value = 0
  generatePrintTemplate.value = false
  setLayer({ objType: 'none' })

  emit('loaded')
})

onBeforeUnmount(() => {
  // Remove the resize listener to prevent a memory leak
  window.removeEventListener('resize', resize)
})

defineExpose({
  fabric,
  generatePrintTemplate,
  addImage,
  addRogueImage,
  addText,
  moveForward,
  moveBackwards,
  generateFabricObject,
  setBackgroundColor,
  setLayer,
  setTextureBackground,
  remove,
  updateTextColor,
  updateTextFont,
  updateTextSize,
})
</script>
<style lang="scss" scoped>
.zeus-sleeve {
  position: relative;
}

.zeus-canvas-container {
  position: absolute;
  top: 0;
  left: 0;
}
</style>
